import React, { useState } from 'react';
import { AIAgent } from '../../../types';
import { toggleFavoriteAgent } from '../../../redux/ai/aiAgentSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import ErrorModal from '../../../components/common/ErrorModal';
import { Heart, Pencil, Trash2 } from 'lucide-react';
import { TaskyButton } from '../../../components/tasky';
import { useToast } from '../../../contexts/ToastContext';

interface AgentCardProps {
  agent: AIAgent;
  onClick?: () => void;
  onEdit?: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent, onClick, onEdit, onDelete }) => {
  const { showToast } = useToast();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const { user } = useAppSelector(state => state.auth);

  // エラーモーダル用の状態
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState({ title: '', message: '' });

  // ベースカラーが利用可能かどうかをチェック
  const baseColor = agent.base_color || '#3498db';

  // Check if current user can edit/delete this agent
  const canEdit = user && agent.created_by_user_id === user.id;

  // エージェントのカテゴリに基づくアイコンを選択
  const getIconByCategory = (category?: string) => {
    switch(category?.toLowerCase()) {
      case '企画・提案':
        return (
          <svg
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
            <path d="M2 2l7.586 7.586"></path>
            <circle cx="11" cy="11" r="2"></circle>
          </svg>
        );
      case 'クリエイティブ':
        return (
          <svg
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
          </svg>
        );
      case 'マーケティング':
        return (
          <svg
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <circle cx="12" cy="12" r="6"></circle>
            <circle cx="12" cy="12" r="2"></circle>
          </svg>
        );
      case '分析・レポート':
        return (
          <svg
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <polyline points="18 20 18 10 12 4 6 10 6 20"></polyline>
            <line x1="12" y1="4" x2="12" y2="20"></line>
          </svg>
        );
      case 'メディア':
        return (
          <svg
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
        );
      case 'sns・コンテンツ':
      case 'sns':
      case 'コンテンツ':
        return (
          <svg
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
          </svg>
        );
      case '運用':
        return (
          <svg
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        );
      default:
        return (
          <svg
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="3" y1="9" x2="21" y2="9"></line>
            <line x1="9" y1="21" x2="9" y2="9"></line>
          </svg>
        );
    }
  };

  // お気に入りトグル処理
  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation(); // カード自体のクリックイベントを防止

    if (!isAuthenticated) {
      // 認証されていない場合は操作をスキップし、オプションでログインを促す
      showToast('お気に入り機能を使用するにはログインが必要です', 'error');
      return;
    }

    // dispatch前にローカルUIを更新してユーザー体験を向上
    const currentStatus = agent.is_favorited || false;

    // 星アイコンの見た目をすぐに切り替え（agentオブジェクトは変更せず、CSSクラスのみ操作）
    const agentCardStar = e.currentTarget as HTMLElement;
    if (currentStatus) {
      agentCardStar.classList.remove('active');
    } else {
      agentCardStar.classList.add('active');
    }

    // ローカル状態変数として見た目のみ変更（実際のデータは変更しない）
    // Reduxが状態を更新したらUIが再レンダリングされるため、一時的な視覚効果のみを提供

    try {
      // Redux状態を更新 (APIレスポンスを待たずにUIを更新)
      dispatch(toggleFavoriteAgent(agent.id))
        .unwrap()
        .catch((error: any) => {
          console.error(`お気に入りトグル API エラー (id: ${agent.id}):`, error);
          // エラー情報をモーダルで表示するために設定
          setErrorDetails({
            title: 'お気に入り登録エラー',
            message: `エージェント "${agent.title}" のお気に入り状態の更新中にエラーが発生しました。\n\nエラー詳細: ${error?.message || 'サーバーとの通信に失敗しました'}`
          });
          setShowErrorModal(true);
        });
    } catch (error: any) {
      console.error(`お気に入りトグル処理エラー (id: ${agent.id}):`, error);
      // エラーモーダルを表示
      setErrorDetails({
        title: 'お気に入り処理エラー',
        message: `エージェント "${agent.title}" のお気に入り状態の更新中に内部エラーが発生しました。\n\nエラー詳細: ${error?.message || 'システムエラーが発生しました'}`
      });
      setShowErrorModal(true);

      // UI状態はそのまま維持（ユーザー体験を優先）
    }
  };

  return (
    <>
      <div
        className="bg-white border border-warm-border rounded-tasky-md p-5 hover:shadow-card transition-all duration-200 cursor-pointer relative"
        onClick={onClick}
      >
        {/* Header with Icon, Title, Category and Favorite Button */}
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl"
            style={{ backgroundColor: `${baseColor}1A`, color: baseColor }}
          >
            {getIconByCategory(agent.category)}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-dark truncate m-0">
              {agent.title}
            </h3>
            {agent.category && (
              <span className="inline-block text-xs bg-warm-surface text-gray-500 rounded-full px-2 py-0.5 mt-0.5">
                {agent.category}
              </span>
            )}
          </div>
          <button
            className={`flex-shrink-0 p-1.5 rounded-full transition-transform hover:scale-110 ${
              agent.is_favorited
                ? 'text-destructive'
                : 'text-gray-300 hover:text-destructive'
            } ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleToggleFavorite}
            aria-label={agent.is_favorited ? 'お気に入りから削除' : 'お気に入りに追加'}
            title={!isAuthenticated ? 'お気に入り機能を使用するにはログインが必要です' : ''}
          >
            {isAuthenticated && agent.is_favorited ? (
              <Heart className="w-5 h-5" fill="currentColor" />
            ) : (
              <Heart className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-500 mt-3 mb-0 line-clamp-2">
          {agent.description || 'No description available'}
        </p>

        {/* Footer with Usage and Actions */}
        <div className="flex flex-col gap-3 mt-4 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-gray-400">
            {agent.usage_count !== undefined ? `利用回数: ${agent.usage_count.toLocaleString()}回` : '利用回数: 0回'}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <>
                <button
                  onClick={onEdit}
                  className="p-1.5 rounded-tasky-xs text-gray-400 hover:text-primary hover:bg-warm-hover transition-colors"
                  title="編集"
                  aria-label="エージェントを編集"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={onDelete}
                  className="p-1.5 rounded-tasky-xs text-gray-400 hover:text-destructive hover:bg-destructive/10 transition-colors"
                  title="削除"
                  aria-label="エージェントを削除"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
            <TaskyButton
              size="sm"
              className="h-auto w-full px-3 py-1.5 text-xs sm:w-auto"
              onClick={onClick}
            >
              チャットを始める
            </TaskyButton>
          </div>
        </div>
      </div>

      {/* エラーモーダル */}
      <ErrorModal
        show={showErrorModal}
        title={errorDetails.title}
        message={errorDetails.message}
        onClose={() => setShowErrorModal(false)}
      />
    </>
  );
};

export default AgentCard;
